"use client";

import { c as _c } from "react/compiler-runtime";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { defaultLocale, locales, normalizeLocale, type LocaleCode } from "@/config/locales";
import i18n, { loadLanguage } from "@/lib/i18n";
interface UseLocaleResult {
  currentLocale: LocaleCode;
  localeData: (typeof locales)[LocaleCode];
  changeLanguage: (locale: string) => Promise<void>;
  isLoaded: boolean;
}
export function useLocale() {
  const $ = _c(14);
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const {
    i18n: i18nInstance
  } = useTranslation();
  const [isLoaded, setIsLoaded] = useState(false);
  let t0;
  if ($[0] !== i18nInstance.resolvedLanguage) {
    t0 = normalizeLocale(i18nInstance.resolvedLanguage);
    $[0] = i18nInstance.resolvedLanguage;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const currentLocale = t0;
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      let isMounted = true;
      const initialize = async () => {
        const urlLocale = typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("locales") : null;
        const target = normalizeLocale(urlLocale ?? i18n.resolvedLanguage);
        await loadLanguage(target);
        await i18n.changeLanguage(target);
        document.documentElement.lang = target;
        if (isMounted) {
          setIsLoaded(true);
        }
      };
      initialize();
      return () => {
        isMounted = false;
      };
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== searchParams) {
    t2 = [searchParams];
    $[3] = searchParams;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  if ($[5] !== pathname || $[6] !== router || $[7] !== searchParams) {
    t3 = async locale => {
      const normalized = normalizeLocale(locale);
      await loadLanguage(normalized);
      await i18n.changeLanguage(normalized);
      const params = new URLSearchParams(searchParams.toString());
      params.set("locales", normalized);
      router.replace(`${pathname}?${params.toString()}`);
      document.cookie = `locales=${normalized};path=/;max-age=31536000`;
      window.localStorage.setItem("locales", normalized);
      document.documentElement.lang = normalized;
    };
    $[5] = pathname;
    $[6] = router;
    $[7] = searchParams;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const changeLanguage = t3;
  const localeData = locales[currentLocale] ?? locales[defaultLocale];
  let t4;
  if ($[9] !== changeLanguage || $[10] !== currentLocale || $[11] !== isLoaded || $[12] !== localeData) {
    t4 = {
      currentLocale,
      localeData,
      changeLanguage,
      isLoaded
    };
    $[9] = changeLanguage;
    $[10] = currentLocale;
    $[11] = isLoaded;
    $[12] = localeData;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
}